const ROOT = '/studentplayground/api/teacher/personal-notes/classes';
const copy = value => JSON.parse(JSON.stringify(value));

export function createPersonalNotesApi({ apiBase, token, fetchImpl = fetch }) {
  async function request(path, method = 'GET', payload) {
    const response = await fetchImpl(`${apiBase}${path}`, {
      method, cache: 'no-store', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    });
    if (response.status === 204) return null;
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(typeof data.detail === 'string' ? data.detail : 'Nepavyko išsaugoti arba įkelti užrašų. Bandykite dar kartą.');
      error.status = response.status;
      throw error;
    }
    return data;
  }
  const classPath = event => `${ROOT}/${encodeURIComponent(event)}/`;
  return {
    read: event => request(classPath(event)),
    save: (event, payload) => request(classPath(event), 'PUT', payload),
    remove: (event, revision) => request(classPath(event), 'DELETE', { revision }),
    history: (event, cursor) => request(`${classPath(event)}history/${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
    template: (event, payload) => request(`${classPath(event)}recurring-sections/`, 'PATCH', payload),
    adminList: (teacher, cursor) => request(`/studentplayground/api/admin/teachers/${teacher}/personal-notes/${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
    adminRead: (teacher, note) => request(`/studentplayground/api/admin/teachers/${teacher}/personal-notes/${note}/`),
  };
}

/** Serialized note/template saves. Edits made in flight remain dirty until acknowledged. */
export function createNoteAutosaver({ initial, writeNote, writeTemplate, onChange = () => {}, delay = 600 }) {
  let noteGeneration = 0, savedNoteGeneration = 0, templateGeneration = 0, savedTemplateGeneration = 0;
  let timer, running, disposed = false;
  const state = {
    sections: copy(initial.sections), headings: copy(initial.template.headings),
    revision: initial.revision, templateRevision: initial.template.revision,
    exists: initial.exists, dirty: false, saving: false, error: '', conflict: false,
  };
  function notify() {
    state.dirty = noteGeneration !== savedNoteGeneration || templateGeneration !== savedTemplateGeneration;
    if (!disposed) onChange({ ...state });
  }
  async function run() {
    state.saving = true;
    state.error = '';
    notify();
    try {
      while (state.dirty && !disposed) {
        if (noteGeneration !== savedNoteGeneration) {
          const generation = noteGeneration;
          const result = await writeNote({ revision: state.revision, sections: copy(state.sections) });
          state.revision = result.revision;
          state.exists = true;
          savedNoteGeneration = generation;
        }
        if (templateGeneration !== savedTemplateGeneration) {
          const generation = templateGeneration;
          const result = await writeTemplate({ revision: state.templateRevision, headings: copy(state.headings) });
          state.templateRevision = result.revision;
          savedTemplateGeneration = generation;
        }
        notify();
      }
      return !state.dirty;
    } catch (error) {
      state.error = error.message;
      state.conflict = error.status === 409;
      return false;
    } finally {
      state.saving = false;
      notify();
    }
  }
  return {
    state,
    edit(sections, headings) {
      state.sections = copy(sections);
      noteGeneration++;
      if (headings !== undefined) { state.headings = copy(headings); templateGeneration++; }
      notify();
      clearTimeout(timer);
      if (!state.conflict) timer = setTimeout(() => this.flush(), delay);
    },
    async flush() {
      clearTimeout(timer);
      if (running) return running;
      if (state.conflict) return false;
      if (!state.dirty) return true;
      running = run();
      try { return await running; } finally { running = null; }
    },
    dispose() { disposed = true; clearTimeout(timer); },
  };
}

export function classDescription(info) {
  if (!info) return '';
  const date = info.starts_at ? new Intl.DateTimeFormat('lt-LT', {
    timeZone: 'Europe/Vilnius', dateStyle: 'medium', timeStyle: 'short',
  }).format(new Date(info.starts_at)) : 'Laikas nenurodytas';
  return `${date} · ${info.students.map(row => row.name).join(', ')}`;
}
